/**
 * Picked images are re-encoded in the browser before upload: decoded, cropped to a centered square, redrawn at
 * 512 × 512 and exported again (JPEG, or PNG when the source may be transparent). What leaves the device is fresh
 * pixels only: no metadata (location, camera), no original bytes. The server checks the result again.
 */

export class ImageError extends Error {}

const MAX_SOURCE_BYTES = 20 * 1024 * 1024;
/** What the upload route accepts (api/agents.ts). */
const MAX_UPLOAD_BYTES = 2 * 1024 * 1024;

async function decode(file: File): Promise<CanvasImageSource & { width: number; height: number }> {
  try {
    return await createImageBitmap(file);
  } catch {
    // Older Safari: through an <img> (the CSP allows blob: images).
    const url = URL.createObjectURL(file);
    try {
      const img = new Image();
      img.src = url;
      await img.decode();
      return Object.assign(img, { width: img.naturalWidth, height: img.naturalHeight });
    } finally {
      URL.revokeObjectURL(url);
    }
  }
}

function toBlob(canvas: HTMLCanvasElement, type: string, quality?: number): Promise<Blob> {
  return new Promise((resolve, reject) => canvas.toBlob((b) => (b ? resolve(b) : reject(new ImageError("Impossible de préparer l'image."))), type, quality));
}

async function base64Of(blob: Blob): Promise<string> {
  const bytes = new Uint8Array(await blob.arrayBuffer());
  let binary = "";
  for (let i = 0; i < bytes.length; i += 0x8000) binary += String.fromCharCode(...bytes.subarray(i, i + 0x8000));
  return btoa(binary);
}

/** The picked file as a 512 × 512 square, base64 (no data: prefix). Throws ImageError with a French message. */
export async function squareImage(file: File, size = 512): Promise<string> {
  if (file.size > MAX_SOURCE_BYTES) throw new ImageError("Image trop lourde (20 Mo maximum).");
  if (file.type && !file.type.startsWith("image/")) throw new ImageError("Choisis une image (PNG, JPEG, WebP…).");
  let source: CanvasImageSource & { width: number; height: number };
  try {
    source = await decode(file);
  } catch {
    throw new ImageError("Ce format d'image n'est pas lisible ici. Essaie une image PNG ou JPEG.");
  }
  const side = Math.min(source.width, source.height);
  if (!side) throw new ImageError("Image vide.");
  const canvas = document.createElement("canvas");
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new ImageError("Impossible de préparer l'image.");
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = "high";
  const transparent = file.type === "image/png" || file.type === "image/webp" || file.type === "image/gif";
  if (!transparent) {
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, size, size);
  }
  ctx.drawImage(source, (source.width - side) / 2, (source.height - side) / 2, side, side, 0, 0, size, size);
  if ("close" in source && typeof source.close === "function") source.close();
  let blob = await toBlob(canvas, transparent ? "image/png" : "image/jpeg", 0.9);
  if (blob.size > MAX_UPLOAD_BYTES) blob = await toBlob(canvas, "image/jpeg", 0.85);
  if (blob.size > MAX_UPLOAD_BYTES) throw new ImageError("Image trop lourde, même réduite.");
  return base64Of(blob);
}
